import React, { ComponentType, SyntheticEvent, useState } from 'react';

import { Helmet } from 'react-helmet';

import { Box, Button, Container } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';
import SectionNavigation from '~/components/Common/Elements/SectionNavigation';
import SectionNavigationLayout from '~/components/Common/Elements/SectionNavigationLayout';
import VendorSearchForm from '~/components/Common/Forms/VendorSearchForm';
import PaginatedPromotionList from '~/components/Common/PaginatedPromotionList';
import PaginatedRatePlanList from '~/components/Common/PaginatedRatePlanList';
import PermissionedComponent from '~/components/Common/PermissionedComponent';
import ChainsListPage from '~/components/Vendors/Chains/ChainsListPage';

import featureToggle from '~/utils/featureToggle';

import ChainsSearchContainer from './ChainsSearchContainer';
import NewRentalModal from './NewRentalModal';
import PropertiesCsvUploadModal from './PropertiesCsvUploadModal';
import PropertiesSearchContainer from './PropertiesSearchContainer';
import PropertyRelationsContainer from './PropertyRelationsContainer';
import RatePlansContainer from './RatePlansContainer';

const TAB_VENDOR = 'vendor';

interface TabConfig {
  title: string;
  component: ComponentType<any>;
  props?: Record<string, any>;
}

const TabsMap: Record<string, TabConfig> = {
  [TAB_VENDOR]: {
    title: 'Vendors',
    component: VendorSearchForm,
  },
  chains: {
    title: 'Chains',
    component: ChainsSearchContainer,
  },
  properties: {
    title: 'Properties',
    component: PropertiesSearchContainer,
  },
  'rate-plans': {
    title: 'Rate Plans',
    component: RatePlansContainer,
  },
  relations: {
    title: 'Relations',
    component: PropertyRelationsContainer,
  },
  extranet: {
    title: 'Extranet Added Rate Plans',
    component: PaginatedRatePlanList,
    props: {
      source: 'extranet',
    },
  },
  'extranet-added-promotions': {
    title: 'Extranet Added Promotions',
    component: PaginatedPromotionList,
    props: {
      source: 'extranet',
    },
  },
} as const;

type TabKey = keyof typeof TabsMap;

export default function VendorsPageContainer() {
  const [key, setKey] = useState<TabKey>(TAB_VENDOR);
  const [isNewRentalModalVisible, setIsNewRentalModalVisible] = useState(false);
  const [isPropertiesCsvModalVisible, setIsPropertiesCsvModalVisible] = useState(false);

  const isChainRateRulesEnabled = featureToggle.availableToShow('CHAIN_RATE_RULES_ENABLED');

  const tabs = {
    ...TabsMap,
    ...(isChainRateRulesEnabled ? { chains: { title: 'Chains', component: ChainsListPage } } : {}),
  };

  const currentTab = tabs[key];

  const title = `Vendors / ${currentTab.title}`;
  const tabKeys = Object.keys(tabs);

  const handleChange = (_: SyntheticEvent, newKey: TabKey) => {
    setKey(newKey);
  };

  const showNewRentalModal = () => {
    setIsNewRentalModalVisible(true);
  };

  const hideNewRentalModal = () => {
    setIsNewRentalModalVisible(false);
  };

  const newRentalEnabled = featureToggle.availableToShow('RENTALS_ENABLED');

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>{title}</title>
      </Helmet>

      <PageHeader title={title}>
        <PermissionedComponent>
          <Button variant="contained" onClick={() => setIsPropertiesCsvModalVisible(true)} sx={{ marginLeft: 'auto' }}>
            Upload Properties
          </Button>
        </PermissionedComponent>
        {newRentalEnabled && (
          <Button variant="contained" onClick={showNewRentalModal} sx={{ marginLeft: 2 }}>
            New Rental
          </Button>
        )}
      </PageHeader>

      <SectionNavigationLayout
        navigation={
          <SectionNavigation
            label="Vendors sections"
            currentTab={key}
            groups={[{ items: tabKeys.map((id) => ({ id, label: tabs[id].title })) }]}
            onSelect={(event, id) => {
              event.preventDefault();
              handleChange(event, id);
            }}
          />
        }
      >
        {' '}
        <Box pt={4}>
          <Box component={currentTab.component} {...(tabs[key].props || {})} />
        </Box>
      </SectionNavigationLayout>
      <NewRentalModal isModalOpen={isNewRentalModalVisible} closeModal={hideNewRentalModal} />
      <PropertiesCsvUploadModal
        isModalOpen={isPropertiesCsvModalVisible}
        closeModal={() => setIsPropertiesCsvModalVisible(false)}
      />
    </Container>
  );
}
